import {
  DropDownButton,
  DropDownButtonItemClickEvent,
} from "@progress/kendo-react-buttons";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import React, {
  useState,
  useCallback,
  ReactNode,
  useMemo,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { If, Then } from "react-if";
import LoadingPanel from "@/components/LodingPanel";
import useOnClickOutside from "@/hooks/useOnClickOutside";

interface TabStripTabsContextValue<T extends TabItem = TabItem> {
  selectedItem: T | undefined;
  setSelectedItem: (item: T | undefined) => void;
  items: T[];
}

const TabStripTabsContext = createContext<TabStripTabsContextValue>({
  selectedItem: undefined,
  setSelectedItem: () => {},
  items: [],
});

interface TabStripTabsProviderProps<T extends TabItem = TabItem> {
  tabs: T[];
  selectedKey?: string;
  children: ReactNode;
}

export const TabStripTabsProvider = <T extends TabItem>({
  tabs,
  selectedKey,
  children,
}: TabStripTabsProviderProps<T>) => {
  const enabledTabs = useMemo(
    () => tabs.filter(tab => !tab.isDisabled),
    [tabs],
  );
  const [selectedItem, setSelectedItem] = useState<T | undefined>(undefined);

  useEffect(() => {
    const item = enabledTabs.find(tab => tab.key === selectedKey) as
      | T
      | undefined;
    setSelectedItem(item);
  }, [selectedKey, enabledTabs]);

  const contextValue = useMemo(
    () => ({
      selectedItem,
      setSelectedItem: setSelectedItem as (item: TabItem | undefined) => void,
      items: enabledTabs,
    }),
    [selectedItem, enabledTabs],
  );

  return (
    <TabStripTabsContext.Provider value={contextValue}>
      {children}
    </TabStripTabsContext.Provider>
  );
};

// any is expected because the Component type is expected to be a React component
// and the component props are expected to be a record of unknown values

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TabItem<T = any> {
  key: string;
  title: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component?: React.ComponentType<T & any>;
  isDisabled?: boolean;
  isDisabledView?: boolean;
  content?: React.ReactNode;
  componentProps?: T;
}

interface TabStripTabsProps {
  tabs: TabItem[];
  selectedKey?: string;
  onTabChange?: (key: string, index: number) => void;
  className?: string;
  isLoading?: boolean;
  enableOverflow?: boolean;
  renderMoreTitle?: (overflowCount: number) => ReactNode;
}

const defaultRenderMoreTitle = (n: number) => `More +${n}`;

const TabStripTabs: React.FC<TabStripTabsProps> = props => {
  const enabledTabs = useMemo(
    () => props.tabs.filter(tab => !tab.isDisabled),
    [props.tabs],
  );

  if (props.enableOverflow) {
    return <OverflowTabStrip {...props} enabledTabs={enabledTabs} />;
  }
  return <SimpleTabStrip {...props} enabledTabs={enabledTabs} />;
};

interface InternalProps extends TabStripTabsProps {
  enabledTabs: TabItem[];
}

/**
 * Controlled whenever the caller owns the selection - it passes `selectedKey`
 * and an `onTabChange` that updates it (usually by navigating) - so a click
 * only *asks* for a tab. A tab change the route refuses (an unsaved-changes
 * guard) then leaves the current tab, and its draft, mounted instead of
 * swapping content before the navigation is attempted. The same rule
 * `OverflowTabStrip` follows. Without both, the strip keeps its own selection.
 */
const SimpleTabStrip: React.FC<InternalProps> = ({
  enabledTabs,
  selectedKey,
  onTabChange,
  className,
  isLoading,
}) => {
  const keyIndex = enabledTabs.findIndex(tab => tab.key === selectedKey);
  const [localSelected, setLocalSelected] = useState<number>(() =>
    Math.max(0, keyIndex),
  );
  // A key naming no enabled tab (bare URL, tab not ready) falls back to the
  // strip's own selection.
  const isControlled = !!onTabChange && keyIndex > -1;
  const selected = isControlled ? keyIndex : localSelected;

  // An uncontrolled strip still follows a `selectedKey` its caller changes.
  useEffect(() => {
    if (keyIndex > -1) setLocalSelected(keyIndex);
  }, [keyIndex]);

  const handleSelect = useCallback(
    (e: TabStripSelectEventArguments) => {
      if (!isControlled) setLocalSelected(e.selected);
      onTabChange?.(enabledTabs[e.selected].key, e.selected);
    },
    [enabledTabs, isControlled, onTabChange],
  );

  return (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      className={`${className} tlx-tab-strip-tabs`}
    >
      {enabledTabs.map(
        (
          { title, Component, content, componentProps, isDisabledView },
          idx,
        ) => (
          <TabStripTab
            key={idx}
            title={title}
            disabled={isLoading || isDisabledView}
          >
            <If condition={selected === idx && !isLoading}>
              <Then>
                {Component ? <Component {...componentProps} /> : content}
              </Then>
            </If>
            <If condition={selected === idx && isLoading}>
              <Then>
                <div style={{ width: "100%", height: "100%" }}>
                  <LoadingPanel isLoading={true} />
                </div>
              </Then>
            </If>
          </TabStripTab>
        ),
      )}
    </TabStrip>
  );
};

const ghostStyle: React.CSSProperties = {
  position: "absolute",
  visibility: "hidden",
  pointerEvents: "none",
  overflow: "hidden",
  whiteSpace: "nowrap",
  left: -9999,
  top: 0,
  width: "max-content",
};

const OVERFLOW_SAFETY_PX = 0;

const noop = () => {};

const OverflowTabStrip: React.FC<InternalProps> = ({
  enabledTabs,
  selectedKey,
  onTabChange,
  className,
  isLoading,
  renderMoreTitle = defaultRenderMoreTitle,
}) => {
  const visibleStripRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const moreButtonWrapperRef = useRef<HTMLDivElement>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // The actual width available inside the visible Kendo `.k-tabstrip-items` ul
  // — what tabs really have to fit in, after Kendo's own padding/border.
  const [budgetWidth, setBudgetWidth] = useState<number>(0);
  // Right edge of each tab in the ghost row, relative to the items wrapper's
  // left edge. Includes any flex gap / margin Kendo applies between items.
  const [tabRights, setTabRights] = useState<number[]>([]);
  const [moreWidth, setMoreWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const wrapper = visibleStripRef.current;
    if (!wrapper) return;
    const ul = wrapper.querySelector<HTMLElement>(".k-tabstrip-items");
    if (!ul) return;
    const measure = () => setBudgetWidth(ul.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(ul);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const node = ghostRef.current;
    if (!node) return;
    const measure = () => {
      const ulEl = node.querySelector<HTMLElement>(".k-tabstrip-items");
      if (!ulEl) return;
      const items = Array.from(ulEl.children) as HTMLElement[];
      if (items.length < enabledTabs.length + 1) return;
      const ulLeft = ulEl.getBoundingClientRect().left;
      const rights = items
        .slice(0, enabledTabs.length)
        .map(el => el.getBoundingClientRect().right - ulLeft);
      const moreEl = items[enabledTabs.length];
      setTabRights(rights);
      setMoreWidth(moreEl?.getBoundingClientRect().width ?? 0);
    };
    measure();
    // Re-measure after fonts/styles settle (font load can shift widths a few px)
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [enabledTabs]);

  const selectedIndex = useMemo(() => {
    if (!selectedKey) return 0;
    const idx = enabledTabs.findIndex(t => t.key === selectedKey);
    return idx >= 0 ? idx : 0;
  }, [selectedKey, enabledTabs]);

  const { visibleIndices, overflowIndices } = useMemo(() => {
    const all = enabledTabs.map((_, i) => i);
    const measured = budgetWidth > 0 && tabRights.length === enabledTabs.length;

    if (!measured) {
      return { visibleIndices: all, overflowIndices: [] as number[] };
    }

    const totalWidth = tabRights[tabRights.length - 1] ?? 0;
    if (totalWidth + OVERFLOW_SAFETY_PX <= budgetWidth) {
      return { visibleIndices: all, overflowIndices: [] as number[] };
    }

    const budget = Math.max(0, budgetWidth - moreWidth - OVERFLOW_SAFETY_PX);
    const visible: number[] = [];
    for (let i = 0; i < tabRights.length; i++) {
      if (tabRights[i] <= budget) {
        visible.push(i);
      } else {
        break;
      }
    }
    if (visible.length === 0) visible.push(0);

    if (!visible.includes(selectedIndex)) {
      visible.pop();
      visible.push(selectedIndex);
      visible.sort((a, b) => a - b);
    }

    const visibleSet = new Set(visible);
    const overflow = all.filter(i => !visibleSet.has(i));
    return { visibleIndices: visible, overflowIndices: overflow };
  }, [budgetWidth, tabRights, moreWidth, enabledTabs, selectedIndex]);

  const visibleTabs = useMemo(
    () => visibleIndices.map(i => enabledTabs[i]),
    [visibleIndices, enabledTabs],
  );
  const overflowTabs = useMemo(
    () => overflowIndices.map(i => enabledTabs[i]),
    [overflowIndices, enabledTabs],
  );

  const selectedInVisible = useMemo(() => {
    const idx = visibleIndices.indexOf(selectedIndex);
    return idx >= 0 ? idx : 0;
  }, [visibleIndices, selectedIndex]);

  const handleSelect = useCallback(
    (e: TabStripSelectEventArguments) => {
      const tab = visibleTabs[e.selected];
      if (!tab) return;
      const originalIndex = visibleIndices[e.selected];
      onTabChange?.(tab.key, originalIndex);
    },
    [visibleTabs, visibleIndices, onTabChange],
  );

  const handleOverflowItemClick = useCallback(
    (e: DropDownButtonItemClickEvent) => {
      const tab = overflowTabs[e.itemIndex];
      if (!tab || tab.isDisabledView) return;
      const originalIndex = overflowIndices[e.itemIndex];
      onTabChange?.(tab.key, originalIndex);
    },
    [overflowTabs, overflowIndices, onTabChange],
  );

  const overflowItemRender = useCallback(
    (props: { item: TabItem; itemIndex: number }) => <>{props.item.title}</>,
    [],
  );

  useOnClickOutside(moreButtonWrapperRef, event => {
    const popup = document.querySelector<HTMLElement>(".tlx-v3-tab-more-popup");
    if (popup?.contains(event.target as Node)) return;
    setIsMoreOpen(false);
  });

  return (
    <div style={{ width: "100%" }}>
      <div ref={ghostRef} aria-hidden style={ghostStyle}>
        <TabStrip className={className} selected={0} onSelect={noop}>
          {enabledTabs.map(tab => (
            <TabStripTab key={tab.key} title={tab.title} />
          ))}
          <TabStripTab
            key="__ghost_more__"
            title={renderMoreTitle(enabledTabs.length)}
          />
        </TabStrip>
      </div>

      <div ref={visibleStripRef}>
        <TabStrip
          selected={selectedInVisible}
          onSelect={handleSelect}
          className={`${className} tlx-tab-strip-tabs`}
        >
          {visibleTabs.map((tab, idx) => {
            const {
              title,
              Component,
              content,
              componentProps,
              isDisabledView,
            } = tab;
            return (
              <TabStripTab
                key={tab.key}
                title={title}
                disabled={isLoading || isDisabledView}
              >
                <If condition={selectedInVisible === idx && !isLoading}>
                  <Then>
                    {Component ? <Component {...componentProps} /> : content}
                  </Then>
                </If>
                <If condition={selectedInVisible === idx && isLoading}>
                  <Then>
                    <div style={{ width: "100%", height: "100%" }}>
                      <LoadingPanel isLoading={true} />
                    </div>
                  </Then>
                </If>
              </TabStripTab>
            );
          })}
          {overflowTabs.length > 0 && (
            <TabStripTab
              key="more-dropdown"
              title={
                <div ref={moreButtonWrapperRef}>
                  <DropDownButton
                    disabled={isLoading}
                    opened={isMoreOpen}
                    onOpen={() => setIsMoreOpen(true)}
                    onClose={() => setIsMoreOpen(false)}
                    text={renderMoreTitle(overflowTabs.length)}
                    popupSettings={{ popupClass: "tlx-v3-tab-more-popup" }}
                    items={overflowTabs.map(t => ({
                      ...t,
                      disabled: !!t.isDisabledView,
                    }))}
                    onItemClick={handleOverflowItemClick}
                    itemRender={overflowItemRender}
                    fillMode="flat"
                    style={{
                      padding: 0,
                      boxShadow: "none",
                      maxHeight: 49,
                    }}
                  />
                </div>
              }
            />
          )}
        </TabStrip>
      </div>
    </div>
  );
};

export const useTabStripTabs = <T extends TabItem>() => {
  const { selectedItem, items } = useContext(TabStripTabsContext);
  const selected = selectedItem || (items[0] as T | undefined);
  return { selectedItem: selected as T, items };
};

export default TabStripTabs;
